import React, { ReactNode } from "react";
import { Navigate, NavigateFunction } from "react-router-dom";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react";
import { AccountService } from "@mindmemobile/mm-payment-modules";
import { fundraiserStore } from "../stores";

type IProps = {
  location: Location;
  navigate: NavigateFunction;
};
type IState = {
  showLoader: boolean;
};

class FindAccount extends React.Component<IProps, IState> {
  // Guards against multiple find-account API hits: this is a MobX observer under a router,
  // so it re-renders on unrelated store/route changes. The old componentWillReceiveProps
  // fired findAccount() on every one of those re-renders. The lookup only needs to run once
  // per mount (the component takes no meaningful props that would change the result).
  private hasLookedUp = false;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLoader: true,
    };
  }

  componentDidMount() {
    this.findAccount();
  }

  toggleLoader = (showLoader: boolean) => {
    this.setState({ showLoader });
    if (!showLoader && !AuthService.fromPaymentSystem && window.parent) {
      const { innerWidth: width, innerHeight: height } = window;
      const message = {
        type: "showHeader",
        width,
        height,
        showIframe: true,
      };
      window.parent.postMessage(JSON.stringify(message), "*");
    }
  };

  findAccount() {
    if (this.hasLookedUp) return;
    this.hasLookedUp = true;
    if (!AuthService.mindmeAccountDetails) {
      // No account context to look up — don't sit on the loader forever.
      // Route to the dashboard, which renders the create-merchant screen
      // when no account/merchant account exists.
      this.toggleLoader(false);
      this.props.navigate("/fundraiser/dashboard");
      return;
    }
    const { subAccountId, parentAccountId, userId } =
      AuthService.mindmeAccountDetails;
    AccountService.findAccount(subAccountId, parentAccountId, userId).subscribe(
      (response) => {
        // response.result was dereferenced unguarded. A response without a
        // result threw inside this handler, which skipped both the
        // toggleLoader and the navigate below and left the page sitting on the
        // loader forever with no way out.
        const result = response?.result;
        if (result?.success && result.accountId) {
          AuthService.setAuthData(
            result.accountId,
            result.shortUniqueId,
            result.isPrimarySubAccount,
            result.hasLibraryManagePermissions
          );
        }
        this.toggleLoader(false);
        this.props.navigate("/fundraiser/dashboard");
      },
      (error) => {
        console.log("Error finding account", error);
        // On failure (e.g. no account exists yet) still route to the
        // dashboard so the create-merchant screen is shown instead of an
        // endless loader.
        this.toggleLoader(false);
        this.props.navigate("/fundraiser/dashboard");
      }
    );
  }

  render(): ReactNode {
    // This page has no content of its own -- it looks the account up and
    // redirects. Rendering <></> once showLoader went false meant that any
    // moment spent still mounted here was a BLANK WHITE SCREEN: the app header
    // and nav are rendered by an ancestor, so only the content area emptied.
    // That is the "fundraiser sometimes doesn't load" symptom, and it stranded
    // the user with nothing to click if the navigate below did not take effect.
    // Keep the loader up for the whole life of this component instead; it
    // unmounts as soon as the redirect lands.
    return <>{new Loader(true).loaderComponent}</>;
  }
}

export default withRouter(observer(FindAccount));
